import React, { useCallback, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import apiLibrary from "services/api";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { closeManageConnectionProtocolModal } from "store/modals/reducer.actions";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import ManageConnectionModalForm from "./ManageConnectionModalForm";
import { Toasts } from "view/components/Toasts";
import { fetchProtocolsAction } from "store/protocols";
import { useParams } from "react-router-dom";
import usePermissions from "hooks/usePermissions";
import { protocolManangeConnectionSchema } from "utils/validationSchemas";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#ffff",
  borderRadius: "10px",
};

// Program Interface
interface ProgramItem {
  value: string;
  label: string;
}

interface FormItem {
  value: string;
  label: string;
}

/**
 * Component for adding, editing, or using a form as a template.
 * @returns {JSX.Element} The component's JSX representation
 */

const ManageConnectionProtocolModal = () => {
  const { manageConnectioProtocolModal } = useSelector(
    (state: RootState) => state.modals
  );

  const [programList, setProgramList] = useState<ProgramItem[]>([]);
  const [formList, setFormsList] = useState<FormItem[]>([]);
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const { programId } = useParams();
  const { protocols } = usePermissions();
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState("");
  const [disconnectProtocols, setDisConnectProtocol] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true); // To track if there's more data

  // Fetch Protocol Profile and Programs on component mount
  useEffect(() => {
    if (manageConnectioProtocolModal.isOpen) {
      if (manageConnectioProtocolModal.data.programId.value !== "") {
        fetchProtocolFormsByPrograms(
          manageConnectioProtocolModal.data.programId
        );
      }
    }
  }, [manageConnectioProtocolModal.isOpen]);
  const handleSubmit = async (
    values: any,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const formValues = {
        ...values,
        id: manageConnectioProtocolModal.data.id,
      };

      if (values.programId) {
        formValues.programId = values.programId.value;
      }

      if (values.formId?.length) {
        formValues.formId = values.formId[0]?.value;
      }

      if (formValues.programId) {
        let disconnectSuccessful = true;
        if (disconnectSuccessful && protocols.canConnectProgramForm) {
          try {
            const { data, message } =
              await apiLibrary.Protocols.connectProtocol(
                manageConnectioProtocolModal?.data.id,
                formValues
              );
            Toasts.success(message);
            dispatch(fetchProtocolsAction(programId));
          } catch (error: any) {
            const errorMsg = error?.response?.data?.message ?? error.message;
            Toasts.error(errorMsg);
          }
        } else {
          Toasts.error("Disconnect failed, connection not attempted.");
        }
        handleClose();
      } else {
        if (!protocols.canDisconnectProgramForm) {
          return;
        }
        dispatch(fetchProtocolsAction(programId));
        handleClose()
      }
    } catch (error: any) {
      // Handle API errors
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      dispatch(fetchProtocolsAction(programId));
      setSubmitting(false);
    }
  };

  /**
   * Handle modal closure.
   * @example
   * handleClose();
   */
  const handleClose = () => {
    dispatch(closeManageConnectionProtocolModal());
  };

  const fetchProtocolPrograms = async (page: number, searchQuery?: string) => {
    setLoading(true);
    try {
      const res: any = await apiLibrary.Protocols.getAllPrograms(
        searchQuery,
        page
      );
      const programs = res.data?.programs?.map((program: any) => {
        return {
          value: program.id,
          label: program.name,
        };
      });
      setProgramList((prevPrograms: any) => {
        const existingProgramIds = new Set(
          prevPrograms.map((program: any) => program.value)
        );
        const uniquePrograms = programs.filter(
          (newProgram: any) => !existingProgramIds.has(newProgram.value)
        );

        return [...prevPrograms, ...uniquePrograms];
      });

      // Check if more data is available (based on your API response)
      if (res.data.totalPages > page) {
        setHasMore(true); // More pages available
      } else {
        setHasMore(false); // No more pages
      }
    } catch (error: any) {
      // Handle API errors
      console.error("Error fetching programs:", error);
    } finally {
      setLoading(false);
    }
  };

  // get forms by program
  const fetchProtocolFormsByPrograms = useCallback(
    async (programId: any, searchString?: string) => {
      try {
        const res = await apiLibrary.Protocols.getFormsByPrograms(
          programId.value ? programId.value : programId,
          searchString
        );
        const forms = res.data?.forms?.map((form: any) => {
          return {
            value: form.formId,
            label: form.formName,
          };
        });
        setFormsList(forms);
      } catch (error: any) {
        // Handle API errors
        console.error("Error fetching forms:", error);
      }
    },
    [formList]
  );

  useEffect(() => {
    fetchProtocolPrograms(page, searchString);
  }, [page, searchString]);

  const handleProgramSearch = (query: string) => {
    setSearchString(query);
  };

  // Load more options when the dropdown reaches the bottom
  const handleMenuScrollToBottom = () => {
    if (hasMore && !loading) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <Modal
      open={manageConnectioProtocolModal.isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      disableAutoFocus={true}
      aria-describedby="modal-modal-description"
      className="border-none"
    >
      <Box sx={style} className="dark:bg-secondaryLight ">
        <Formik
          initialValues={manageConnectioProtocolModal?.data}
          validationSchema={protocolManangeConnectionSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {(formikProps) => (
            <Form>
              <ManageConnectionModalForm
                {...formikProps}
                handleClose={handleClose}
                programList={programList}
                handleProgramSearch={handleProgramSearch}
                fetchProtocolFormsByPrograms={fetchProtocolFormsByPrograms}
                setFormsList={setFormsList}
                formsList={formList}
                handleMenuScrollToBottom={handleMenuScrollToBottom}
                loading={loading}
                setDisConnectProtocol={setDisConnectProtocol}
              />
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default ManageConnectionProtocolModal;
